<template>
  <div class="app-container">
    <el-card>
      <div class="header d-flex justify-content-between align-items-center mb-3">
        <h2>Займы (Год: {{ formData.trancheYear }})</h2>
        <div>
          <el-button @click="goBack" class="ms-2">Назад</el-button>
          <el-button type="primary" @click="saveData" :loading="saving" v-if="!isRectorAndAccountantAndManagerAndAdminRO">Сохранить</el-button>
        </div>
      </div>

      <div class="table-container mb-3" style="max-width: 600px;" @paste="handlePaste" @keydown="handleKeydown">
        <table class="table table-bordered grid-table">
          <thead>
            <tr>
              <th style="width: 150px;">Дата</th>
              <th>Сумма уплаченного вознагражения</th>
            </tr>
          </thead>
          <tbody>
            <tr class="calculated-row">
              <td style="text-align: right; font-weight: bold;">ВСЕГО:</td>
              <td style="font-weight: bold;">{{ formatNumber(totalCalculatedSum) }}</td>
            </tr>
            <tr v-for="(month, index) in months" :key="index">
              <td class="text-center">{{ month.label }}</td>
              <td :data-item-idx="index" data-col="0">
                <el-input 
                  v-model="formData[month.key]" 
                  size="small" 
                  :formatter="formatInputNumber" 
                  :parser="parseInputNumber" 
                  :disabled="isRectorAndAccountantAndManagerAndAdminRO"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </el-card>
    <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
  </div>
</template>

<script>
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import { showErrorNotify, showSuccessNotify } from "@/utils/notificationUtils";
import service from "@/services/ueLoansService";
import authService from "@/services/authService";
import delegationService from "@/services/delegationService";
import {handleGridKeydown} from "@/utils/gridKeyboardNav";

export default {
  name: "UeLoansEditView",
  components: { VueLoading },
  data() {
    return {
      isLoading: false,
      saving: false,
      uuid: this.$route.params.uuid,
      months: [
        { label: 'январь', key: 'janSum' },
        { label: 'февраль', key: 'febSum' },
        { label: 'март', key: 'marSum' },
        { label: 'апрель', key: 'aprSum' },
        { label: 'май', key: 'maySum' },
        { label: 'июнь', key: 'junSum' },
        { label: 'июль', key: 'julSum' },
        { label: 'август', key: 'augSum' },
        { label: 'сентябрь', key: 'sepSum' },
        { label: 'октябрь', key: 'octSum' },
        { label: 'ноябрь', key: 'novSum' },
        { label: 'декабрь', key: 'decSum' }
      ],
      formData: {
        janSum: '', febSum: '', marSum: '', aprSum: '', maySum: '', junSum: '',
        julSum: '', augSum: '', sepSum: '', octSum: '', novSum: '', decSum: '',
        calculatedSum: ''
      }
    };
  },
  computed: {
    isRectorAndAccountantAndManagerAndAdminRO() {
      return delegationService.isRector() || delegationService.isAccountant() || delegationService.isManagerFC() || delegationService.isAdminReadOnly();
    },
    totalCalculatedSum() {
      const sum = this.months.reduce((acc, month) => {
        return acc + (parseFloat(this.formData[month.key]) || 0);
      }, 0);
      this.formData.calculatedSum = sum.toFixed(2);
      return sum;
    }
  },
  async mounted() {
    this.loadData();
  },
  methods: {
    formatNumber(val) {
      if (val === undefined || val === null || val === '' || isNaN(val)) return '0,00';
      const formatted = parseFloat(val).toFixed(2);
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    },
    formatInputNumber(value) {
      if (value === undefined || value === null || value === '') return '';
      const parts = value.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    },
    parseInputNumber(value) {
      if (!value) return '';
      let parsed = value.toString().replace(',', '.');
      parsed = parsed.replace(/[^\d.]/g, '');
      const parts = parsed.split('.');
      if (parts.length > 2) {
        parsed = parts[0] + '.' + parts.slice(1).join('');
      }
      return parsed;
    },
    async loadData() {
      if (!this.uuid) return;
      this.isLoading = true;
      try {
        const data = await service.getById(this.uuid);
        if (data) {
          this.formData = Object.assign({}, this.formData, data);
        }
      } catch (e) {
        showErrorNotify('Ошибка при загрузке данных');
      } finally {
        this.isLoading = false;
      }
    },
    async saveData() {
      this.saving = true;
      try {
        const result = await service.save(this.formData);
        this.formData = Object.assign({}, this.formData, result);
        showSuccessNotify('Данные успешно сохранены');
      } catch (e) {
        showErrorNotify('Ошибка при сохранении');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push('/expenses/loans');
    },
    handleKeydown(event) {
      handleGridKeydown(event, this.$el);
    },
    handlePaste(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT' || this.isRectorAndAccountantAndManagerAndAdminRO) return;

      const td = target.closest('td');
      if (!td) return;

      const startItemIdx = parseInt(td.dataset.itemIdx);
      if (isNaN(startItemIdx)) return;

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (!text) return;

      const rows = text.split(/\r?\n/).filter(r => r.trim() !== '');
      if (rows.length <= 1 && !rows[0].includes('\t')) return;

      event.preventDefault();

      let currentItemIdx = startItemIdx;

      for (let r = 0; r < rows.length; r++) {
        const cols = rows[r].split('\t');
        
        if (currentItemIdx >= this.months.length) break;
        const monthKey = this.months[currentItemIdx].key;

        const rawVal = String(cols[0]).replace(/\s/g, '').replace(',', '.');
        const num = parseFloat(rawVal);
        this.formData[monthKey] = isNaN(num) || num === 0 ? '' : num;

        currentItemIdx++;
      }

      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.grid-table th { font-size: 13px; vertical-align: middle; text-align: center; background-color: #e8f5e9 !important; }
.grid-table td { font-size: 13px; vertical-align: middle; }
.calculated-row td { background-color: #e8f5e9 !important; text-align: center; }
:deep(.el-input__inner) { font-size: 13px; }
</style>
